import { ErrorHandlerService } from '@adaptiva/core/error-handler';
import { MenuController, MenuItem } from '@adaptiva/core/menus';
import { OverlayService } from '@adaptiva/core/overlays';
import { SnackbarService } from '@adaptiva/core/snackbars';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { LlmConversationNode } from '../classes';
import { ELlmTabs } from '../enums';
import { ILlmConversationNode } from '../interfaces';
import { AidaService, LlmChatService, LlmForkService, LlmHistoryService } from '../services';

@Injectable()
export abstract class BaseLlmNodeMenuController extends MenuController<LlmConversationNode> {

	public readonly goToChatMenuName: string = 'Show in Chat';

	public readonly showHistoryMenuName: string = 'Show in History';

	public readonly createForkMenuName: string = 'Create Fork';

	protected readonly aida: AidaService = inject(AidaService);

	protected readonly chatService: LlmChatService = inject(LlmChatService);

	protected readonly forks: LlmForkService = inject(LlmForkService);

	protected readonly overlays: OverlayService = inject(OverlayService);

	protected readonly snackbars: SnackbarService = inject(SnackbarService);

	protected readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);

	protected readonly llmHistoryService: LlmHistoryService = inject(LlmHistoryService);

	public generate(_node: LlmConversationNode): Observable<MenuItem<LlmConversationNode>[]> {
		return of([
			this.getOpenInChatMenuItem(_node),
			this.getCreateForkMenuItem(_node),
			this.getOpenInHistoryMenuItem(_node),
		]);
	}

	public getOpenInChatMenuItem(_node: LlmConversationNode): MenuItem<LlmConversationNode> {
		const _itemId: string = this.hashItem(this.goToChatMenuName, _node);
		const _item: MenuItem<LlmConversationNode> = this.getItem(_itemId, _node) || this.createItem({
			id: _itemId,
			name: this.goToChatMenuName,
			data: _node,
			onSelect: this.openInChat.bind(this),
			isDisabled: new BehaviorSubject(!this.aida.getConversationNode(_node.nodeId))
		});

		return _item;
	}

	public getCreateForkMenuItem(_node: LlmConversationNode): MenuItem<LlmConversationNode> {
		const _itemId: string = this.hashItem(this.createForkMenuName, _node);
		let _descriptionText: string = 'Create Fork';

		if (!this.aida.getConversationNode(_node.nodeId))
			_descriptionText = 'Cannot create fork';

		const _item: MenuItem<LlmConversationNode> = this.getItem(_itemId, _node) || this.createItem({
			id: _itemId,
			name: this.createForkMenuName,
			data: _node,
			description: _descriptionText,
			onSelect: this.createFork.bind(this),
			isDisabled: new BehaviorSubject(!this.aida.getConversationNode(_node.nodeId))
		});

		return _item;
	}

	public getOpenInHistoryMenuItem(_node: LlmConversationNode): MenuItem<LlmConversationNode> {
		const _itemId: string = this.hashItem(this.showHistoryMenuName, _node);
		const _item: MenuItem<LlmConversationNode> = this.getItem(_itemId, _node) || this.createItem({
			id: _itemId,
			name: this.showHistoryMenuName,
			data: _node,
			onSelect: this.openInHistory.bind(this),
			isDisabled: new BehaviorSubject(!this.aida.getConversationNode(_node.nodeId))
		});

		return _item;
	}

	/** Returns a hash to serve as a unique identifier/lookup for a menu item */
	protected override hashItem(_key: string, _node: LlmConversationNode): string {
		return `${super.hashItem(_key, _node)}-${_node?.nodeId ?? crypto.randomUUID()}`;
	}

	protected openInChat(_item: MenuItem<LlmConversationNode>): void {
		this.aida.currentTab.set(ELlmTabs.CHAT);
		const _node: LlmConversationNode = _item?.data;

		if (_node)
			this.chatService.goToNode(_node);
	}

	protected createFork(_item: MenuItem<LlmConversationNode>): void {
		this.overlays.prompt({
			title: 'Name your Fork',
			/** @todo: improve the label text here */
			label: 'This name will be used as the artifact title when you create your first dashboard in this fork.',
			validators: [(_name: string) => !!_name?.trim()],
			type: 'text'
		}).pipe(
			this.takeOneUntilDestroyed(),
			switchMap((_newName: string) => {
				return this.forks.createFork({
					conversationId: this.aida.conversation().objectId,
					forkName: _newName.trim(),
					nodeId: _item.data.nodeId
				});
			})
		).subscribe({
			next: (_forkNode: ILlmConversationNode) => {
				const _addedNode: LlmConversationNode = this.aida.addConversationNode(_item.data.parent.nodeId, _forkNode);

				this.forks.activeFork.set(_addedNode);
				this.snackbars.display('Created New Fork');
			},
			error: (e: Error) => {
				this.errorHandler.defaultErrorHandler(e, 'BaseLlmNodeMenuController.createFork');
			}
		});
	}

	protected openInHistory(_item: MenuItem<LlmConversationNode>): void {
		this.aida.currentTab.set(ELlmTabs.HISTORY);

		this.llmHistoryService.setViewboxTarget(_item.data.nodeId);
	}

}
